import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { api } from "@/lib/convexApi";

import {
  MasterTableFooter,
  MasterToolbar,
  useMasterFilter,
} from "./master-filter";

export interface BillVendor {
  _id: string;
  displayName: string;
  qboPayeeName: string;
  billingAddress?: string;
  paymentMethod?: string;
  invoiceDiscountTerms?: string;
  defaultPaymentTerms?: string;
  aliases?: string[];
  emailDomains?: string[];
  defaultGlAccountId?: string;
  active: boolean;
}

interface GlAccountOption {
  _id: string;
  qboAccountName: string;
  active: boolean;
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

interface VendorFormState {
  displayName: string;
  qboPayeeName: string;
  billingAddress: string;
  paymentMethod: string;
  invoiceDiscountTerms: string;
  defaultPaymentTerms: string;
  aliases: string;
  emailDomains: string;
  defaultGlAccountId: string;
}

const EMPTY_FORM: VendorFormState = {
  displayName: "",
  qboPayeeName: "",
  billingAddress: "",
  paymentMethod: "",
  invoiceDiscountTerms: "",
  defaultPaymentTerms: "",
  aliases: "",
  emailDomains: "",
  defaultGlAccountId: "",
};

export function VendorsTab({
  authUserId,
  canManage,
}: {
  authUserId: string | undefined;
  canManage: boolean;
}) {
  const vendors = useQuery(
    api.accountingBillMasters.listVendors,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as BillVendor[] | undefined;

  // Inactive accounts are fetched too: a vendor may still point at one that
  // was deactivated later, and hiding it would render the field blank while
  // the value is still set — an edit the user never made, rejected on save.
  const glAccounts = useQuery(
    api.accountingBillMasters.listGlAccounts,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as GlAccountOption[] | undefined;
  const glNameById = React.useMemo(
    () => new Map((glAccounts ?? []).map((a) => [a._id, a.qboAccountName])),
    [glAccounts],
  );

  const filter = useMasterFilter<BillVendor>(
    vendors,
    (vendor) =>
      `${vendor.displayName} ${vendor.qboPayeeName} ${(vendor.aliases ?? []).join(" ")} ${(vendor.emailDomains ?? []).join(" ")}`,
  );

  const counts = React.useMemo(() => {
    const all = vendors ?? [];
    const active = all.filter((vendor) => vendor.active).length;
    return { active, inactive: all.length - active, all: all.length };
  }, [vendors]);

  const createVendor = useMutation(api.accountingBillMasters.createVendor);
  const updateVendor = useMutation(api.accountingBillMasters.updateVendor);
  const setVendorActive = useMutation(
    api.accountingBillMasters.setVendorActive,
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<BillVendor | null>(null);
  const [form, setForm] = React.useState<VendorFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = React.useState(false);

  // Only active accounts are offered, plus whichever one this vendor already
  // has, so an existing default stays visible and clearable.
  const glOptions = React.useMemo(
    () =>
      (glAccounts ?? [])
        .filter(
          (account) =>
            account.active || account._id === form.defaultGlAccountId,
        )
        .map((account) => ({
          value: account._id,
          label: account.active
            ? account.qboAccountName
            : `${account.qboAccountName} (inactive)`,
        })),
    [glAccounts, form.defaultGlAccountId],
  );


  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (vendor: BillVendor) => {
    setEditing(vendor);
    setForm({
      displayName: vendor.displayName,
      qboPayeeName: vendor.qboPayeeName,
      billingAddress: vendor.billingAddress ?? "",
      paymentMethod: vendor.paymentMethod ?? "",
      invoiceDiscountTerms: vendor.invoiceDiscountTerms ?? "",
      defaultPaymentTerms: vendor.defaultPaymentTerms ?? "",
      aliases: (vendor.aliases ?? []).join(", "),
      emailDomains: (vendor.emailDomains ?? []).join(", "),
      defaultGlAccountId: vendor.defaultGlAccountId ?? "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!authUserId) return;
    setIsSaving(true);
    try {
      const shared = {
        billingAddress: form.billingAddress,
        paymentMethod: form.paymentMethod,
        invoiceDiscountTerms: form.invoiceDiscountTerms,
        defaultPaymentTerms: form.defaultPaymentTerms,
        aliases: splitCsv(form.aliases),
        emailDomains: splitCsv(form.emailDomains),
      };
      if (editing) {
        await updateVendor({
          authUserId,
          vendorId: editing._id,
          displayName: form.displayName,
          qboPayeeName: form.qboPayeeName,
          // null clears the default; undefined would mean "leave unchanged".
          defaultGlAccountId: form.defaultGlAccountId || null,
          ...shared,
        });
        toast.success("Vendor updated");
      } else {
        await createVendor({
          authUserId,
          displayName: form.displayName,
          qboPayeeName: form.qboPayeeName,
          defaultGlAccountId: form.defaultGlAccountId || undefined,
          ...shared,
        });
        toast.success("Vendor created");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save vendor");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (vendor: BillVendor) => {
    if (!authUserId) return;
    try {
      await setVendorActive({
        authUserId,
        vendorId: vendor._id,
        active: !vendor.active,
      });
      toast.success(vendor.active ? "Vendor deactivated" : "Vendor activated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not update vendor",
      );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          QBO payees. Bills snapshot vendor values at submission, so edits here
          never rewrite history.
        </p>
        {canManage && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New vendor
          </Button>
        )}
      </div>

      <MasterToolbar
        search={filter.search}
        onSearchChange={filter.setSearch}
        status={filter.status}
        onStatusChange={filter.setStatus}
        counts={counts}
        placeholder="Search name, alias or email domain"
      />

      {vendors === undefined || filter.visible === undefined ? (
        <Skeleton className="h-32 w-full" />
      ) : vendors.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No vendors yet. Use the Import tab to bootstrap the catalog from
          QuickBooks.
        </p>
      ) : filter.visible.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
          No vendors match this filter.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Display name</TableHead>
                <TableHead>QBO payee</TableHead>
                <TableHead>Default GL</TableHead>
                <TableHead>Terms</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="w-32" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filter.visible.map((vendor) => (
                <TableRow key={vendor._id}>
                  <TableCell className="py-1.5 font-medium">
                    {vendor.displayName}
                  </TableCell>
                  <TableCell className="py-1.5">{vendor.qboPayeeName}</TableCell>
                  <TableCell
                    className="text-muted-foreground max-w-64 truncate py-1.5"
                    title={
                      vendor.defaultGlAccountId
                        ? glNameById.get(vendor.defaultGlAccountId)
                        : undefined
                    }
                  >
                    {(vendor.defaultGlAccountId &&
                      glNameById.get(vendor.defaultGlAccountId)) ??
                      "—"}
                  </TableCell>
                  <TableCell className="py-1.5">
                    {vendor.defaultPaymentTerms ?? "—"}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Badge variant={vendor.active ? "secondary" : "outline"}>
                      {vendor.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="py-1.5">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Edit vendor"
                          onClick={() => openEdit(vendor)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(vendor)}
                        >
                          {vendor.active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {filter.filtered !== undefined && (
        <MasterTableFooter
          shown={filter.visible?.length ?? 0}
          total={filter.filtered.length}
          hasMore={filter.hasMore}
          onShowMore={filter.showMore}
          onShowAll={filter.showAll}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit vendor" : "New vendor"}</DialogTitle>
            <DialogDescription>
              The QBO payee name must exactly match the payee in QuickBooks.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="vendor-display-name">Display name</Label>
              <Input
                id="vendor-display-name"
                value={form.displayName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, displayName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vendor-qbo-payee">QBO payee name</Label>
              <Input
                id="vendor-qbo-payee"
                value={form.qboPayeeName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, qboPayeeName: e.target.value }))
                }
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="vendor-billing-address">Billing address</Label>
              <Textarea
                id="vendor-billing-address"
                value={form.billingAddress}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    billingAddress: e.target.value,
                  }))
                }
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vendor-payment-method">Payment method</Label>
              <Input
                id="vendor-payment-method"
                value={form.paymentMethod}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    paymentMethod: e.target.value,
                  }))
                }
                placeholder="e.g. ACH, Check"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vendor-default-terms">
                Default payment terms
              </Label>
              <Input
                id="vendor-default-terms"
                value={form.defaultPaymentTerms}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    defaultPaymentTerms: e.target.value,
                  }))
                }
                placeholder="e.g. Net 30"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="vendor-discount-terms">
                Invoice discount terms
              </Label>
              <Input
                id="vendor-discount-terms"
                value={form.invoiceDiscountTerms}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    invoiceDiscountTerms: e.target.value,
                  }))
                }
                placeholder="e.g. 2/10 Net 30"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Default GL account</Label>
              <ComboboxSelect
                options={glOptions}
                value={form.defaultGlAccountId}
                placeholder="No default"
                searchPlaceholder="Search GL accounts…"
                emptyText="No GL accounts found."
                disablePortal
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    // Re-picking the current value clears it.
                    defaultGlAccountId:
                      value === prev.defaultGlAccountId ? "" : value,
                  }))
                }
              />
              <p className="text-muted-foreground text-xs">
                Prefills the first allocation row when a bill picks this vendor.
                Always editable on the bill.
              </p>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="vendor-aliases">Aliases (comma-separated)</Label>
              <Input
                id="vendor-aliases"
                value={form.aliases}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, aliases: e.target.value }))
                }
                placeholder="Acme Inc, Acme Co"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="vendor-email-domains">
                Email domains (comma-separated)
              </Label>
              <Input
                id="vendor-email-domains"
                value={form.emailDomains}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, emailDomains: e.target.value }))
                }
                placeholder="acme.com, billing.acme.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                isSaving || !form.displayName.trim() || !form.qboPayeeName.trim()
              }
            >
              {isSaving ? "Saving…" : editing ? "Save vendor" : "Create vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
